/* eslint-disable no-prototype-builtins */
import React, { useEffect } from 'react';
import { Controller, useWatch } from 'react-hook-form';
import {
  Grid,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormControlLabel,
  Switch,
  Autocomplete,
  Stack,
  Typography,
  Paper,
  Chip,
  Box,
  FormHelperText,
  Alert
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import {
  duckBreedOptions,
  chickenBreedOptions,
  speciesOptions,
  sexOptions,
  varietyOptions
} from '../../constants/EntryFormOptions';
import {
  selectFilterByFavoriteAndFoundation,
  selectFilteredEntriesBySex
} from '../../store/slices/entryFormSlice';
import { useEntriesQuery } from '../../hooks/useFirebaseQueries';

const EntryForm = ({ form }) => {
  const { control, watch, setValue } = form;
  const watchedSpecies = useWatch({
    name: 'species',
    control,
    defaultValue: ''
  });

  const { data: birdEntries, isSuccess: isEntriesQuerySuccess } = useEntriesQuery();


  console.log('birdEntries', birdEntries);

  return (
    <Grid container spacing={2}>
      {isEntriesQuerySuccess && (
        <>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Controller
              name="name"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  label="Name *"
                  placeholder="Enter name"
                  error={!!error}
                  helperText={error?.message}
                  fullWidth
                />
              )}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Controller
              name="sex"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <FormControl fullWidth error={!!error}>
                  <InputLabel>Sex</InputLabel>
                  <Select {...field} label="Sex">
                    {sexOptions.map((option) => (
                      <MenuItem key={option} value={option}>
                        {option}
                      </MenuItem>
                    ))}
                  </Select>
                  {error && <FormHelperText>{error.message}</FormHelperText>}
                </FormControl>
              )}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
        <Controller
          name="mother"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <FormControl fullWidth error={!!error}>
              <InputLabel>Mother</InputLabel>
              <Select {...field} label="Mother">
                {birdEntries.filter(birdEntry => birdEntry.sex==="Female").map((bird) => (
                  <MenuItem key={bird.name} value={bird.name}>
                    {bird.name}
                  </MenuItem>
                ))}
              </Select>
              {error && <FormHelperText>{error.message}</FormHelperText>}
            </FormControl>
          )}
        />
      </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
        <Controller
          name="father"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <FormControl fullWidth error={!!error}>
              <InputLabel>Father</InputLabel>
              <Select {...field} label="Father">
                {birdEntries.filter(birdEntry => birdEntry.sex==="Male").map((bird) => (
                  <MenuItem key={bird.name} value={bird.name}>
                    {bird.name}
                  </MenuItem>
                ))}
              </Select>
              {error && <FormHelperText>{error.message}</FormHelperText>}
            </FormControl>
          )}
        />
      </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <Controller
              name="species"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <FormControl fullWidth error={!!error}>
                  <InputLabel>Species</InputLabel>
                  <Select {...field} label="Species">
                    {speciesOptions.map((option) => (
                      <MenuItem key={option} value={option}>
                        {option}
                      </MenuItem>
                    ))}
                  </Select>
                  {error && <FormHelperText>{error.message}</FormHelperText>}
                </FormControl>
              )}
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <Controller
              name="breed"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <FormControl fullWidth error={!!error}>
                  <InputLabel>Breed</InputLabel>
                  <Select {...field} label="Breed">
                    {watchedSpecies === 'Chicken'
                      ? chickenBreedOptions.map((chicken) => (
                          <MenuItem key={chicken} value={chicken}>
                            {chicken}
                          </MenuItem>
                        ))
                      : duckBreedOptions.map((duck) => (
                          <MenuItem key={duck} value={duck}>
                            {duck}
                          </MenuItem>
                        ))}
                  </Select>
                  {error && <FormHelperText>{error.message}</FormHelperText>}
                </FormControl>
              )}
            />
          </Grid>

          {/* Variety field - color/pattern subdivision within breed */}
          <Grid size={{ xs: 12, sm: 6 }}>
            <Controller
              name="variety"
              control={control}
              render={({ field }) => (
                <Autocomplete
                  options={varietyOptions}
                  value={field.value || null}
                  onChange={(_, newValue) => field.onChange(newValue)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Variety"
                      placeholder="Select variety (color/pattern)"
                      helperText="Color or pattern subdivision within the breed (e.g., Silver Laced, Buff, Barred)"
                    />
                  )}
                />
              )}
            />
          </Grid>

          {/* Hatch Date field - essential for age calculations and gender terminology */}
          <Grid size={{ xs: 12, sm: 6 }}>
            <Controller
              name="hatchDate"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <DatePicker
                  {...field}
                  label="Hatch Date"
                  value={field.value ? dayjs(field.value) : null}
                  onChange={(newValue) => {
                    field.onChange(
                      newValue ? newValue.format('YYYY-MM-DD') : null
                    );
                  }}
                  maxDate={dayjs()}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      error: !!error,
                      helperText:
                        error?.message ||
                        'Date when the bird hatched (used for age calculation and gender terminology)'
                    }
                  }}
                />
              )}
            />
          </Grid>

          <Grid size={{ xs: 12 }}>
            <Controller
              name="notes.active"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  label="Notes"
                  placeholder="Enter notes..."
                  multiline
                  minRows={4}
                  error={!!error}
                  helperText={error?.message}
                  fullWidth
                />
              )}
            />
          </Grid>

          <Grid size={{ xs: 12 }}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Controller
                name="isFavorited"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={<Switch {...field} checked={field.value} />}
                    label="Favorite"
                  />
                )}
              />

              <Controller
                name="isFoundation"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={<Switch {...field} checked={field.value} />}
                    label="Foundation"
                  />
                )}
              />
            </Box>
          </Grid>

          {/* Display archived notes if they exist */}
          <Grid size={{ xs: 12 }}>
            <Controller
              name="notes.archived"
              control={control}
              render={({ field }) => {
                if (!field.value || field.value.length === 0) return null;

                return (
                  <Stack spacing={1}>
                    <Typography variant="h6" fontWeight={500}>
                      Archived Notes
                    </Typography>
                    {field.value.map((note, index) => (
                      <Paper key={index} sx={{ p: 2 }} variant="outlined">
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mb: 1 }}
                        >
                          {note.user} -{' '}
                          {new Date(
                            note.timestamp?.toDate?.() || note.timestamp
                          ).toLocaleDateString()}
                        </Typography>
                        <Typography>{note.content}</Typography>
                      </Paper>
                    ))}
                  </Stack>
                );
              }}
            />
          </Grid>
        </>
      )}
    </Grid>
  );
};

export default EntryForm;
